import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Icon } from "@iconify/react";

const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL || "https://api.mil-168.com"
).replace(/\/+$/, "");

const PAGE_SIZE = 12;

function maskUsername(username) {
  if (!username) return "****";
  if (username.length <= 4) return "****";
  return username.slice(0, username.length - 4) + "****";
}

function StarDisplay({ value }) {
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((n) => (
        <span
          key={n}
          style={{
            fontSize: 15,
            color: n <= value ? "#facc15" : "rgba(255,255,255,0.18)",
          }}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("th-TH", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export default function Reviews() {
  const [reviews, setReviews] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0,
  });
  const [loading, setLoading] = useState(false);

  const fetchReviews = useCallback(async (page) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/reviews/public`, {
        params: { page, limit: PAGE_SIZE },
      });
      setReviews(res.data?.data || []);
      setPagination({
        page: res.data?.pagination?.page || 1,
        totalPages: res.data?.pagination?.totalPages || 1,
        total: res.data?.pagination?.total || 0,
      });
    } catch (_) {
      setReviews([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReviews(1);
  }, [fetchReviews]);

  const goToPage = (p) => {
    fetchReviews(p);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "20px 16px 80px",
        maxWidth: 920,
        margin: "0 auto",
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 24,
          padding: "18px 22px",
          background:
            "linear-gradient(135deg, color-mix(in srgb, var(--primaryColor) 22%, rgba(13,13,20,0.96)) 0%, rgba(13,13,20,0.9) 100%)",
          border:
            "1px solid color-mix(in srgb, var(--primaryColor) 38%, transparent)",
          borderRadius: 20,
          boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
        }}
      >
        <Icon icon="fluent-color:star-emphasis-24" width={34} />
        <div>
          <div style={{ color: "#fff", fontWeight: 800, fontSize: 20 }}>
            รีวิวจากลูกค้า
          </div>
          {pagination.total > 0 && (
            <div
              style={{
                color: "rgba(255,255,255,0.48)",
                fontSize: 13,
                marginTop: 2,
              }}
            >
              {pagination.total.toLocaleString()} รีวิวทั้งหมด
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            padding: "60px 0",
          }}
        >
          <div
            style={{
              width: 40,
              height: 40,
              border: "3px solid rgba(255,255,255,0.12)",
              borderTopColor: "var(--primaryColor, #f97316)",
              borderRadius: "50%",
              animation: "reviews-page-spin 0.7s linear infinite",
            }}
          />
          <style>{`@keyframes reviews-page-spin { to { transform: rotate(360deg); } }`}</style>
        </div>
      ) : reviews.length === 0 ? (
        <div
          style={{
            textAlign: "center",
            padding: "80px 0",
            color: "rgba(255,255,255,0.35)",
            fontSize: 15,
          }}
        >
          <Icon
            icon="solar:star-shine-bold-duotone"
            width={52}
            style={{ opacity: 0.4, display: "block", margin: "0 auto 14px" }}
          />
          ยังไม่มีรีวิว
        </div>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 12,
            marginBottom: 24,
          }}
        >
          {reviews.map((r) => (
            <div
              key={r.id}
              style={{
                background: "rgba(0,0,0,0.45)",
                border: "1px solid rgba(255,255,255,0.14)",
                borderRadius: 16,
                padding: "14px 16px",
                display: "flex",
                flexDirection: "column",
                gap: 8,
                transition: "border-color 0.2s, box-shadow 0.2s",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <span
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    color: "rgba(255,255,255,0.88)",
                  }}
                >
                  {maskUsername(r.username)}
                </span>
                <StarDisplay value={r.rating} />
              </div>
              {r.message && (
                <p
                  style={{
                    margin: 0,
                    fontSize: 13,
                    color: "rgba(255,255,255,0.65)",
                    lineHeight: 1.55,
                    wordBreak: "break-word",
                    display: "-webkit-box",
                    WebkitLineClamp: 4,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {r.message}
                </p>
              )}
              <span
                style={{
                  fontSize: 11,
                  color: "rgba(255,255,255,0.28)",
                  marginTop: "auto",
                }}
              >
                {formatDate(r.created_at)}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 10,
            padding: "16px 0",
          }}
        >
          <button
            type="button"
            disabled={pagination.page <= 1}
            onClick={() => goToPage(pagination.page - 1)}
            style={{
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: 10,
              color: "#fff",
              padding: "8px 20px",
              cursor: pagination.page <= 1 ? "not-allowed" : "pointer",
              opacity: pagination.page <= 1 ? 0.4 : 1,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            ← ก่อนหน้า
          </button>

          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
              .filter((p) => Math.abs(p - pagination.page) <= 2)
              .map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => goToPage(p)}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 9,
                    border:
                      p === pagination.page
                        ? "1px solid var(--primaryColor, #f97316)"
                        : "1px solid rgba(255,255,255,0.14)",
                    background:
                      p === pagination.page
                        ? "var(--primaryColor, #f97316)"
                        : "rgba(255,255,255,0.07)",
                    color: "#fff",
                    fontWeight: p === pagination.page ? 700 : 400,
                    fontSize: 13,
                    cursor: "pointer",
                  }}
                >
                  {p}
                </button>
              ))}
          </div>

          <button
            type="button"
            disabled={pagination.page >= pagination.totalPages}
            onClick={() => goToPage(pagination.page + 1)}
            style={{
              background: "rgba(255,255,255,0.07)",
              border: "1px solid rgba(255,255,255,0.14)",
              borderRadius: 10,
              color: "#fff",
              padding: "8px 20px",
              cursor:
                pagination.page >= pagination.totalPages
                  ? "not-allowed"
                  : "pointer",
              opacity: pagination.page >= pagination.totalPages ? 0.4 : 1,
              fontSize: 13,
              fontWeight: 600,
            }}
          >
            ถัดไป →
          </button>
        </div>
      )}
    </div>
  );
}
